import React from "react";

import L from "leaflet";
import { MapContainer, TileLayer, useMap } from "react-leaflet";

import Section from "./Section";
import { citiesList } from "../data/cities";

interface CityLocation {
  name: string;
  lat: number;
  lng: number;
}

// Actual coordinates of cities in India
const cityLocations: CityLocation[] = [
  { name: "Mumbai", lat: 19.076, lng: 72.8777 },
  { name: "Delhi", lat: 28.7041, lng: 77.1025 },
  { name: "Bengaluru", lat: 12.9716, lng: 77.5946 },
  { name: "Pune", lat: 18.5204, lng: 73.8567 },
  { name: "Kolkata", lat: 22.5726, lng: 88.3639 },
  { name: "Hyderabad", lat: 17.385, lng: 78.4867 },
];

// Create custom icon for markers
const createCustomIcon = (color: string) => {
  return L.divIcon({
    className: "custom-div-icon",
    html: `
      <div style="
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// Component to control map panning based on focus
const MapPanController: React.FC = () => {
  const map = useMap();

  React.useEffect(() => {
    // Disable dragging by default
    map.dragging.disable();

    const mapContainer = map.getContainer();

    const handleFocus = () => {
      // Enable dragging when map is focused
      map.dragging.enable();
    };

    const handleBlur = () => {
      // Disable dragging when map loses focus
      map.dragging.disable();
    };

    // Handle clicks on the map to focus it
    const handleMapClick = (e: L.LeafletMouseEvent) => {
      const target = e.originalEvent.target as HTMLElement;

      // Check if click is NOT on markers or popups
      const isMarker =
        target.closest(".leaflet-marker-icon") ||
        target.closest(".leaflet-marker-pane") ||
        target.closest(".custom-div-icon");
      const isPopup =
        target.closest(".leaflet-popup") ||
        target.closest(".leaflet-popup-pane");

      // If clicking on map background (not markers/popups), focus the container
      if (!isMarker && !isPopup) {
        // Use setTimeout to ensure marker clicks are processed first
        setTimeout(() => {
          mapContainer.focus();
        }, 0);
      }
    };

    // Also handle direct clicks on the map container
    const handleContainerClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is NOT on markers or popups
      const isMarker =
        target.closest(".leaflet-marker-icon") ||
        target.closest(".leaflet-marker-pane") ||
        target.closest(".custom-div-icon");
      const isPopup =
        target.closest(".leaflet-popup") ||
        target.closest(".leaflet-popup-pane");

      // If clicking on map background, focus the container
      if (!isMarker && !isPopup) {
        mapContainer.focus();
      }
    };

    // Add focus and blur event listeners
    mapContainer.addEventListener("focus", handleFocus);
    mapContainer.addEventListener("blur", handleBlur);
    map.on("click", handleMapClick);
    mapContainer.addEventListener("click", handleContainerClick);

    // Make the map container focusable
    mapContainer.setAttribute("tabindex", "0");

    return () => {
      mapContainer.removeEventListener("focus", handleFocus);
      mapContainer.removeEventListener("blur", handleBlur);
      map.off("click", handleMapClick);
      mapContainer.removeEventListener("click", handleContainerClick);
    };
  }, [map]);

  return null;
};

// Custom marker component with both hover and click functionality
const InteractiveMarker: React.FC<{
  city: CityLocation;
  activeCity: string | null;
  setActiveCity: (cityName: string | null) => void;
}> = ({ city, activeCity, setActiveCity }) => {
  const map = useMap();
  const isActive = activeCity === city.name;

  React.useEffect(() => {
    const marker = L.marker([city.lat, city.lng], {
      icon: createCustomIcon("#EC4899"),
    });

    // Create hover popup (quick preview)
    const hoverPopupInstance = L.popup({
      closeButton: false,
      autoClose: false,
      closeOnClick: false,
      className: "hover-popup",
    }).setContent(`
      <div style="text-align: center; padding: 8px; min-width: 150px;">
        <h3 style="font-weight: bold; color: #6B46C1; font-size: 14px; margin: 0 0 4px 0;">
          ${city.name}
        </h3>
        <p style="font-size: 11px; color: #6B7280; margin: 0;">
          Click to join community
        </p>
      </div>
    `);

    // Create click popup (detailed with action)
    const clickPopupInstance = L.popup({
      closeButton: true,
      autoClose: false,
      closeOnClick: false,
      className: "click-popup",
    }).setContent(`
      <div style="text-align: center; padding: 12px; min-width: 200px;">
        <h3 style="font-weight: bold; color: #6B46C1; font-size: 16px; margin: 0 0 8px 0;">
          ${city.name}
        </h3>
        <p style="font-size: 12px; color: #6B7280; margin: 0 0 12px 0;">
          Cohop is operational here!
        </p>
        <a 
          href="#signup-form" 
          onclick="
            const currentUrl = window.location.href.split('?')[0];
            const newUrl = currentUrl + '?city=${encodeURIComponent(city.name)}';
            window.history.replaceState({}, '', newUrl);
            
            // Dispatch custom event to notify form of URL change
            window.dispatchEvent(new CustomEvent('urlChanged', { 
              detail: { city: '${city.name}' } 
            }));
            
            const element = document.getElementById('signup-form');
            if (element) {
              const elementPosition = element.offsetTop - 72;
              window.scrollTo({ top: elementPosition, behavior: 'smooth' });
            }
            return false;
          "
          style="
            display: inline-block;
            background: linear-gradient(135deg, #EC4899, #8B5CF6);
            color: white;
            text-decoration: none;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
            transition: all 0.2s ease;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          "
          onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)'"
          onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)'"
        >
          Join ${city.name} Community
        </a>
      </div>
    `);

    // Add hover events
    marker.on("mouseover", () => {
      // Only show hover popup if this city is not active
      if (!isActive) {
        hoverPopupInstance.setLatLng([city.lat, city.lng]).openOn(map);
      }
    });

    marker.on("mouseout", () => {
      // Only close hover popup if this city is not active
      if (!isActive) {
        map.closePopup(hoverPopupInstance);
      }
    });

    // Add click events
    marker.on("click", () => {
      // Close all other popups first
      map.closePopup();

      // Set this city as active
      setActiveCity(city.name);

      // Open click popup for this city
      clickPopupInstance.setLatLng([city.lat, city.lng]).openOn(map);
    });

    // Show click popup if this city is active
    if (isActive) {
      clickPopupInstance.setLatLng([city.lat, city.lng]).openOn(map);
    }

    marker.addTo(map);

    return () => {
      map.removeLayer(marker);
      map.closePopup(hoverPopupInstance);
      map.closePopup(clickPopupInstance);
    };
  }, [city, map, isActive, setActiveCity]);

  return null;
};

const IndiaMapSection: React.FC = () => {
  // Filter to only show cities that are in our operational list
  const operationalCities = cityLocations.filter((city) =>
    citiesList.some((operationalCity) => operationalCity.name === city.name)
  );

  // State to track which city popup is currently open
  const [activeCity, setActiveCity] = React.useState<string | null>(null);

  return (
    <Section id="cities" height="full" className="border-b border-pink-200">
      <div className="max-w-6xl w-full space-y-12">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-purple-800 font-display mb-4">
            Currently operational in
          </h2>
          <p className="text-lg text-purple-700 max-w-2xl mx-auto">
            Join our verified women-only travel communities across India&apos;s
            major cities
          </p>
        </div>

        {/* Map Container */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative w-full h-96 md:h-[500px] rounded-3xl border-2 border-pink-200 shadow-2xl overflow-hidden z-40">
            <MapContainer
              center={[20.5937, 78.9629]} // Center of India
              zoom={5}
              style={{ height: "100%", width: "100%", zIndex: 40 }}
              className="rounded-3xl"
              dragging={false}
            >
              <MapPanController />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* City Markers with Interactive Popups */}
              {operationalCities.map((city) => (
                <InteractiveMarker
                  key={city.name}
                  city={city}
                  activeCity={activeCity}
                  setActiveCity={setActiveCity}
                />
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default IndiaMapSection;
